import type React from "react";
import type { Ref } from "react";
import { useCallback, useRef, useState } from "react";

import type { DocumentMediaProps } from "@/components/Media/DocumentMedia";
import { DocumentMedia } from "@/components/Media/DocumentMedia";
import { FileInput } from "@/design-system/FileInput";
import { Inline } from "@/design-system/Inline";
import { mergeRefs } from "@/helpers/mergeRefs";

import type { FormDocument } from "./useDocumentInput";
import { useDocumentInput } from "./useDocumentInput";

export interface DocumentInputProps extends Pick<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  "aria-invalid" | "id"
> {
  ref?: Ref<HTMLInputElement>;
  value: FormDocument[];
  accept?: string;
  onChange: (files: FormDocument[]) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  texts?: DocumentMediaProps["texts"];
  disabled?: boolean;
}

/* todo: add ability to add/delete/edit multiple files */
export function DocumentInput({
  value: documents,
  onChange,
  texts,
  ref,
  ...props
}: DocumentInputProps): React.ReactNode {
  const [isDragging, setIsDragging] = useState(false);

  const onDocumentChange = useCallback(
    (files: FormDocument[] | ((oldFiles: FormDocument[]) => FormDocument[])) => {
      onChange?.(Array.isArray(files) ? files : files(documents));
    },
    [documents, onChange],
  );

  const innerRef = useRef<HTMLInputElement>(null);
  const { addDocuments, removeDocuments } = useDocumentInput({
    selectedDocuments: documents,
    maximumFiles: 1,
    onChange: onDocumentChange,
  });
  const openFileDialog = () => innerRef.current?.click();
  const deleteAllFiles = () => {
    if (innerRef.current) {
      innerRef.current.value = "";
      removeDocuments();
      props.onBlur?.();
    }
  };

  return (
    <Inline
      gap="sm"
      wrap={false}
      onDrop={(event) => {
        if (event.dataTransfer.files && innerRef.current) {
          innerRef.current.files = event.dataTransfer.files;
          addDocuments(event.dataTransfer.files);
          setIsDragging(false);

          event.preventDefault();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDragOverCapture={() => setIsDragging(true)}
      onDragLeaveCapture={() => setIsDragging(false)}
    >
      <DocumentMedia
        dropActive={isDragging}
        document={documents[0]}
        aria-invalid={props["aria-invalid"]}
        onAdd={openFileDialog}
        onDelete={deleteAllFiles}
        texts={texts}
        disabled={props.disabled}
      />
      <FileInput
        tabIndex={-1}
        data-testid="document-input"
        {...props}
        ref={mergeRefs(innerRef, ref)}
        onChange={(event) => addDocuments(event.target.files)}
        disabled={props.disabled}
      />
    </Inline>
  );
}
